/**
 * @module features/upload/upload-executor
 * @description Handles file upload execution, progress tracking, and result handling
 */

import { getValidToken } from '../../core/auth/token-manager.js';
import { getBaseUrl } from '../../core/api/api-client.js';
import { getSelectedFiles, markUploadingAsFailed, applyUploadResults } from './upload-form.js';
import { logger } from '../../utils/logger.js';
import { safeMonitoring as monitoring, ErrorCategory, ErrorSeverity } from '../../services/monitoring-safe.js';
import { ENTITY_TYPES } from '../entity/entity-types.js';

/**
 * Upload files to server (entity-aware)
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts, dc-photos)
 * @param {string} entityId - Entity ID
 * @param {Function} onProgressCallback - Optional callback for progress updates
 * @returns {Promise<Object>} Upload result object
 */
export async function uploadFiles(entityType, entityId, onProgressCallback = null) {
    const selectedFiles = getSelectedFiles();

    if (selectedFiles.length === 0) {
        throw new Error('No files selected for upload');
    }

    // Get entity configuration
    const config = ENTITY_TYPES[entityType];

    // Determine which API endpoint to use (searchEntityType or entityType)
    const apiEntityType = config?.searchEntityType || entityType;

    // Build FormData
    const formData = new FormData();

    selectedFiles.forEach((fileItem, index) => {
        const fieldName = `file${index + 1}`;
        formData.append(fieldName, fileItem.file);
        formData.append(`${fieldName}_category`, fileItem.categoryId);

        // Update status
        fileItem.status = 'uploading';
        fileItem.progress = 0;
    });

    // Notify progress callback for initial state
    if (onProgressCallback) {
        onProgressCallback(selectedFiles);
    }

    try {
        // Get fresh token for upload
        const token = await getValidToken();

        // Get base URL from API client
        const baseUrl = getBaseUrl();

        // Send request - Note: fetchWithAuth doesn't work well with FormData
        // so we handle token refresh manually here
        const makeUploadRequest = async (authToken) => fetch(`${baseUrl}/api/${apiEntityType}/${entityId}/files`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`
            },
            body: formData
        });

        let response = await makeUploadRequest(token);

        // If token expired, refresh and retry
        if (response.status === 401) {
            logger.info('Upload token expired, refreshing...');
            const newToken = await getValidToken(true);
            response = await makeUploadRequest(newToken);
        }

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');

            if (response.status === 404) {
                throw new Error('Upload endpoint not found. The upload functionality may not be implemented on the server.');
            } else if (response.status === 405) {
                throw new Error('Upload method not allowed. The server may not support file uploads for this endpoint.');
            } else {
                throw new Error(`Upload failed: ${response.status} ${response.statusText}. Server response: ${errorText}`);
            }
        }

        const result = await response.json();

        // Update file statuses based on results (camelCase properties)
        const successCount = result?.successCount || 0;
        const totalFiles = result?.totalFiles || 0;
        const failureCount = result?.failureCount || (totalFiles - successCount);

        // Per-file detail, including the reason for each failure. Note that the API
        // returns 200 with successCount 0 when every file is rejected, so a failed
        // upload does not necessarily mean a failed request.
        const serverFiles = Array.isArray(result?.files) ? result.files : [];
        const hasExpectedShape = Boolean(result) &&
            (typeof result.totalFiles === 'number' || serverFiles.length > 0);

        if (hasExpectedShape) {
            applyUploadResults(serverFiles, successCount);
        } else {
            // Fallback: mark all as error if response format is unexpected
            markUploadingAsFailed('Server returned unexpected response format');
        }

        // Notify progress callback for final state
        if (onProgressCallback) {
            onProgressCallback(selectedFiles);
        }

        return {
            success: successCount > 0,
            successCount,
            failureCount,
            totalFiles
        };

    } catch (error) {
        logger.error('Upload failed:', error);

        // Track upload error
        if (monitoring.isInitialized()) {
            monitoring.trackError(error, {
                category: ErrorCategory.UPLOAD,
                severity: ErrorSeverity.ERROR,
                operation: 'executeUpload',
                entityType: entityType,
                entityId: entityId,
                fileCount: selectedFiles.length,
                totalSize: selectedFiles.reduce((sum, f) => sum + f.file.size, 0)
            });
        }

        // Mark all files as failed
        markUploadingAsFailed(error.message);

        // Notify progress callback for error state
        if (onProgressCallback) {
            onProgressCallback(selectedFiles);
        }

        throw error;
    }
}

/**
 * Check if upload endpoint is available (for debugging)
 * @param {Object} user - Firebase user object
 * @param {string} entityType - Entity type
 * @param {string} entityId - Entity ID
 * @returns {Promise<boolean>} True if endpoint is available
 */
export async function checkUploadEndpoint(user, entityType, entityId) {
    try {
        const idToken = await user.getIdToken();
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/${entityType}/${entityId}/files`;

        // Make an OPTIONS request to check endpoint
        const response = await fetch(url, {
            method: 'OPTIONS',
            headers: {
                'Authorization': `Bearer ${idToken}`
            }
        });

        return response.ok || response.status === 405; // 405 means endpoint exists but OPTIONS not allowed
    } catch (error) {
        logger.warn('Upload endpoint check failed:', error);
        return true; // Assume available if check fails
    }
}

/**
 * Simulate upload progress (for future enhancement with XHR)
 * Note: fetch API doesn't support upload progress natively
 * This is a placeholder for future XMLHttpRequest implementation
 * @param {Function} _onProgress - Progress callback
 */
export function setupProgressTracking(_onProgress) {
    // TODO: Implement with XMLHttpRequest for true progress tracking
    // For now, fetch API is used which doesn't support upload progress
    logger.debug('Progress tracking not yet implemented with fetch API');
}
